import { blue, grey } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import React from 'react';

declare module '@mui/material/styles' {
  interface TypographyVariantsOptions {
    bold?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    bold: true;
  }
}

export const hexToRgb = (value: string) => {
  let input = value;
  if (!input) return input;
  input = input.replace('#', '');
  const hexRegex = /[0-9A-Fa-f]/g;
  if (!hexRegex.test(input) || (input.length !== 3 && input.length !== 6)) {
    throw new Error('input is not a valid hex color.');
  }
  if (input.length === 3) {
    const first = input[0];
    const second = input[1];
    const last = input[2];
    input = first + first + second + second + last + last;
  }
  input = input.toUpperCase();
  const first = input[0] + input[1];
  const second = input[2] + input[3];
  const last = input[4] + input[5];
  return `${parseInt(first, 16)}, ${parseInt(second, 16)}, ${parseInt(last, 16)}`;
};

const primaryMain = '#181919';
const secondaryMain = '#ffffff';
const errorMain = '#f44336';
const warningMain = '#ff9800';
const successMain = '#4caf50';
const infoMain = '#2196f3';

/**
 * brand colors:
 * Dark red: #651704
 * Light grey: #c1ceda
 * Dark blue: #212a51
 * Green #52a08d
 * Orange: #ec864e
 * Blue: #1263a1
 * Light brown: #bdac94
 * Yellow: #ffc64b
 */

const headerBorder = '1px solid #ebebeb';
const defaultBorder = `1px solid ${grey[400]}`;

export const tableColors = {
  rowAlt: '#f3f7f6',
  header: '#e8eeec',
  borderLight: '#e3e9e7',
  border: '#bbc4c1',
  hoverRow: '#e5ebe9',
  hoverHeader: '#d5dfdb',
  headerAccent: 'rgba(0, 156, 126, 0.2)',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primaryMain,
    },
    secondary: {
      main: secondaryMain,
    },
    error: {
      main: errorMain,
    },
    warning: {
      main: warningMain,
    },
    success: {
      main: successMain,
    },
    info: {
      main: infoMain,
      light: '#85C6F9',
      dark: '#004BD6',
    },
    dianomiGreen: {
      main: '#009c7e',
    },
    dianomiGrey: {
      main: '#f0f0f0',
    },
    /**
     * Publisher charts: hues spaced around the wheel so adjacent legend items don’t read as one family.
     * (Blue ≠ indigo, teal ≠ green, orange ≠ amber/lime, pink ≠ red, violet ≠ fuchsia.)
     */
    pubDashboardComputer: {
      main: '#1D4ED8',
    },
    pubDashboardMobile: {
      main: '#0F766E',
    },
    pubDashboardTablet: {
      main: '#6D28D9',
    },
    pubDashboardApp: {
      main: '#EA580C',
    },
    pubDashboardApple: {
      main: '#EC4899',
    },
    pubDashboardNewsletter: {
      main: '#166534',
    },
    pubDashboardSdk: {
      main: '#DC2626',
    },
    pubDashboardViewability: {
      main: '#475569',
    },
    pubDashboardAmp: {
      main: '#65A30D',
    },
  },
  typography: {
    fontFamily: ['Nexa', 'Source Sans Pro', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    // Reflects the HTML headings sizes
    h1: {
      fontWeight: 'bold',
      fontSize: '2em',
    },
    h2: {
      fontWeight: 'bold',
      fontSize: '1.5em',
    },
    h3: {
      fontWeight: 'bold',
      fontSize: '1.17em',
    },
    h4: {
      fontWeight: 'bold',
      fontSize: '1em',
    },
    h5: {
      fontWeight: 'bold',
      fontSize: '0.82em',
    },
    h6: {
      fontWeight: 'bold',
      fontSize: '0.67em',
    },

    // Custom variants
    bold: { fontSize: '0.9em', fontWeight: '600' },
  },
  components: {
    MuiCheckbox: {
      defaultProps: {
        color: 'success',
        size: 'small',
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {},
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          zIndex: 5000,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .Mui-error': {
            color: grey[700],
            '&. MuiFormLabel.root': {
              color: grey[700],
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&. Mui-disabled': {
            color: grey[700],
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '& .MuiFormControlLabel-label': {
            color: grey[700],
          },
          '&.Mui-disabled': {
            '& .MuiFormControlLabel-label': {
              color: grey[700],
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            color: '#000000',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'collapse',
          overflow: 'unset',
          '& tr:hover': {
            '& td:after': {
              backgroundColor: tableColors.hoverRow,
            },
          },
          '& td': {
            borderBottom: `1px solid ${tableColors.borderLight}`,
            maxWidth: '300px',
            overflowWrap: 'break-word',
            whiteSpace: 'unset',
            overflow: 'unset',
          },
          '& th': {
            borderBottom: `1px solid ${tableColors.border}`,
            padding: '5px 10px',
            '& .MuiTableSortLabel-root': {
              height: '0',
              width: '0',
              opacity: 0,
              transition: 'opacity 0.2s ease-out, height 0.2s ease-out, width 0.2s ease-out',
              '&.Mui-active': {
                opacity: 1,
                height: 'unset',
                width: 'unset',
              },
            },
            '&:hover .MuiTableSortLabel-root:not(.Mui-active)': {
              opacity: 0.4,
              height: 'unset',
              width: 'unset',
            },
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          '&.MuiModal-root': {
            zIndex: 3000,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          bold: 'bold',
        },
      },
    },
  },
  defaultStyles: {
    border: defaultBorder,
    inputStyles: {
      fontFamily: ['Nexa', 'Source Sans Pro', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      border: defaultBorder,
      borderRadius: '4px',
      color: 'rgba(0, 0, 0, 0.87)',
      padding: '8px 10px',
      display: 'flex',
      flexGrow: 1,
      fontSize: '1rem',
      '&:focus': {
        borderColor: blue[800],
      },
      '&[disabled]': {
        background: grey[100],
      },
    },
    drawerWidth: 210,
    drawerWidthMini: 80,
    transition: 'all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)',
    boxShadow: {
      border: '0',
      borderRadius: '3px',
      boxShadow: `0 10px 20px -12px rgba(${hexToRgb(
        primaryMain,
      )}, 0.42), 0 3px 20px 0px rgba(${hexToRgb(
        primaryMain,
      )}, 0.12), 0 8px 10px -5px rgba(${hexToRgb(primaryMain)}, 0.2)`,
      padding: '10px 0',
    },
    scrollBar: {
      '&::-webkit-scrollbar': {
        backgroundColor: '#00000012',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#3c3c3c8f',
        borderRadius: '90px',
      },
    },
    header: {
      backgroundColor: '#f8f8f8cc',
      borderBottom: headerBorder,
      borderLeft: headerBorder,
    },
    errorContainer: {
      padding: '0px 14px',
      fontSize: '11px',
      margin: '4px 0px 0px',
      borderRadius: '5px',
      backgroundColor: '#ff9292',
      color: '#000000',
      whiteSpace: 'normal',
    },
    warningContainer: {
      padding: '0px 14px',
      fontSize: '11px',
      margin: '4px 0px 0px',
      borderRadius: '5px',
      backgroundColor: '#fce3be',
      color: '#000000',
      whiteSpace: 'normal',
    },
    horizontalTab: {
      fontSize: '1em',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      backgroundColor: grey[200],
      color: grey[900],
      '&.Mui-selected': {
        backgroundColor: 'transparent',
      },
      '&:not(:first-of-type)': {
        '&:before': {
          content: '" "',
          position: 'absolute',
          left: 0,
          display: 'block',
          height: 30,
          width: 1,
          zIndex: 1,
          borderLeft: `1px solid ${grey[900]}`,
        },
      },
      '&:not(.Mui-selected)': {
        '&:hover': {
          backgroundColor: grey[100],
        },
      },
    },
    tableButton: {
      textWrap: 'nowrap',
      border: defaultBorder,
      textTransform: 'unset',
      paddingY: '7px',
      maxHeight: '40px',
      position: 'relative',
      icon: {
        color: '#3F82FB',
      },
    },
    table: tableColors,
  },
});

export default theme;
